import { getAuth } from 'firebase/auth';
import app from './firebase';

// Backend API base URL - update this to your deployed backend URL
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'https://rctech-login-backend-807594002571.us-central1.run.app';

// TOTP MFA Service using backend pyotp
export interface MFASecret {
  secret: string;
  qr_code: string;
  backup_codes: string[];
  provisioning_uri: string;
}

export interface MFAStatus {
  enabled: boolean;
  created_at?: string;
  last_verification?: string;
  backup_codes_count: number;
  explicit_logout?: boolean;
}

export class MFAService {
  private static async getAuthHeaders(): Promise<HeadersInit> {
    const auth = getAuth(app);
    const user = auth.currentUser;
    
    if (!user) {
      throw new Error('User not authenticated');
    }
    
    const token = await user.getIdToken();
    
    return {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`
    };
  }

  static async enableMFA(userId: string, email: string): Promise<MFASecret> {
    try {
      const headers = await this.getAuthHeaders();
      
      const response = await fetch(`${BACKEND_URL}/api/mfa/enable`, {
        method: 'POST',
        headers,
        body: JSON.stringify({})
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to enable MFA');
      }
      
      const data = await response.json();
      
      return {
        secret: data.secret,
        qr_code: data.qr_code,
        backup_codes: data.backup_codes,
        provisioning_uri: data.provisioning_uri
      };
    } catch (error) {
      console.error('Error enabling MFA:', error);
      throw new Error(`Failed to enable MFA: ${error}`);
    }
  }

  static async disableMFA(userId: string, code: string): Promise<void> {
    try {
      const headers = await this.getAuthHeaders();
      
      const response = await fetch(`${BACKEND_URL}/api/mfa/disable`, {
        method: 'POST',
        headers,
        body: JSON.stringify({ code })
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to disable MFA');
      }
      
      await response.json();
    } catch (error) {
      console.error('Error disabling MFA:', error);
      throw new Error(`Failed to disable MFA: ${error}`);
    }
  }

  static async getMFAStatus(userId: string, idToken?: string): Promise<MFAStatus> {
    try {
      let headers: HeadersInit;
      
      if (idToken) {
        // Use provided token (from login flow)
        headers = {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${idToken}`
        };
      } else {
        // Use current user's token (from authenticated state)
        headers = await this.getAuthHeaders();
      }
      
      console.log('Making MFA status request to:', `${BACKEND_URL}/api/mfa/status`);
      console.log('With headers:', headers);
      
      const response = await fetch(`${BACKEND_URL}/api/mfa/status`, {
        method: 'GET',
        headers
      });
      
      console.log('MFA status response status:', response.status);
      
      if (!response.ok) {
        const errorData = await response.json();
        console.error('MFA status error response:', errorData);
        throw new Error(errorData.detail || 'Failed to get MFA status');
      }
      
      const data = await response.json();
      console.log('MFA status response data:', data);
      
      return {
        enabled: data.enabled,
        created_at: data.created_at,
        last_verification: data.last_verification,
        backup_codes_count: data.backup_codes_count,
        explicit_logout: data.explicit_logout
      };
    } catch (error) {
      console.error('Error getting MFA status:', error);
      throw new Error(`Failed to get MFA status: ${error}`);
    }
  }

  static async verifyTOTPCode(userId: string, code: string, idToken?: string): Promise<boolean> {
    try {
      let headers: HeadersInit;
      
      if (idToken) {
        // Use provided token (from login flow)
        headers = {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${idToken}`
        };
      } else {
        // Use current user's token (from authenticated state)
        headers = await this.getAuthHeaders();
      }
      
      const response = await fetch(`${BACKEND_URL}/api/mfa/verify`, {
        method: 'POST',
        headers,
        body: JSON.stringify({ code })
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to verify MFA code');
      }
      
      const data = await response.json();
      return data.valid;
    } catch (error) {
      console.error('Error verifying MFA code:', error);
      throw new Error(`Failed to verify MFA code: ${error}`);
    }
  }

  static async verifyBackupCode(userId: string, code: string): Promise<{ valid: boolean; remaining_codes: number }> {
    try {
      const headers = await this.getAuthHeaders();
      
      const response = await fetch(`${BACKEND_URL}/api/mfa/verify-backup`, {
        method: 'POST',
        headers,
        body: JSON.stringify({ code })
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to verify backup code');
      }
      
      const data = await response.json();
      return {
        valid: data.valid,
        remaining_codes: data.remaining_codes
      };
    } catch (error) {
      console.error('Error verifying backup code:', error);
      throw new Error(`Failed to verify backup code: ${error}`);
    }
  }

  static async generateNewBackupCodes(userId: string, code: string): Promise<string[]> {
    try {
      const headers = await this.getAuthHeaders();
      
      const response = await fetch(`${BACKEND_URL}/api/mfa/generate-backup-codes`, {
        method: 'POST',
        headers,
        body: JSON.stringify({ code })
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to generate backup codes');
      }
      
      const data = await response.json();
      return data.backup_codes;
    } catch (error) {
      console.error('Error generating new backup codes:', error);
      throw new Error(`Failed to generate new backup codes: ${error}`);
    }
  }

  static async setLogoutFlag(): Promise<void> {
    try {
      console.log('Setting logout flag...');
      const headers = await this.getAuthHeaders();
      console.log('Auth headers obtained for logout flag');
      
      const response = await fetch(`${BACKEND_URL}/api/mfa/set-logout-flag`, {
        method: 'POST',
        headers
      });
      
      console.log('Logout flag API response status:', response.status);
      
      if (!response.ok) {
        const errorData = await response.json();
        console.error('Logout flag API error response:', errorData);
        throw new Error(errorData.detail || 'Failed to set logout flag');
      }
      
      const result = await response.json();
      console.log('Logout flag set successfully:', result);
    } catch (error) {
      console.error('Error setting logout flag:', error);
      throw new Error(`Failed to set logout flag: ${error}`);
    }
  }

  static async clearLogoutFlag(): Promise<void> {
    try {
      const headers = await this.getAuthHeaders();
      
      const response = await fetch(`${BACKEND_URL}/api/mfa/clear-logout-flag`, {
        method: 'POST',
        headers
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to clear logout flag');
      }
    } catch (error) {
      console.error('Error clearing logout flag:', error);
      throw new Error(`Failed to clear logout flag: ${error}`);
    }
  }

  // Legacy methods for backward compatibility
  static async generateMFASecret(userId: string, userEmail: string): Promise<MFASecret> {
    return this.enableMFA(userId, userEmail);
  }
}